import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Breadcrumbs, PanelCard, Spinner, Typography } from '@glplus/glpweb-dsl';
import { useVendor } from '../../lib/CapabilitiesContext';
import { vendorApiClient, type LocationArchive } from '../../lib/vendorApiClient';

export default function LocationArchives() {
  const { t } = useTranslation();
  const { activeVendorId } = useVendor();
  const [items, setItems] = useState<LocationArchive[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!activeVendorId) return;
    try { setItems((await vendorApiClient.listLocationArchives(activeVendorId)).items); }
    catch (e) { setError((e as Error).message); }
  }, [activeVendorId]);
  useEffect(() => { void load(); }, [load]);
  if (!items) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><Spinner color="brand" /></Box>;

  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 960 }}>
    <Breadcrumbs ariaLabel={t('locations.breadcrumbAria')} linkComponent={Link} items={[{ label: t('nav.locations'), to: '/locations' }, { label: t('locations.archives.title'), ariaCurrent: true }]} />
    <Box><Typography component="h1" variant="h4" sx={{ fontWeight: 800 }}>{t('locations.archives.title')}</Typography><Typography sx={{ color: 'text.secondary' }}>{t('locations.archives.subtitle')}</Typography></Box>
    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
    {items.length === 0 ? <Alert severity="info">{t('locations.archives.empty')}</Alert> : items.map((archive) => {
      const snapshot = archive.snapshot;
      const name = typeof snapshot.name === 'string' ? snapshot.name : t('locations.archives.unknown');
      const address = readAddress(snapshot.address);
      return <PanelCard key={archive.archiveId} title={`${name} · ${t('locations.archives.archived')}`} subtitle={t('locations.archives.archivedOn', { date: new Date(archive.archivedAt).toLocaleDateString() })} accent="#F67A1A" sx={{ height: 'auto' }}>
        {address && <Typography>{address}</Typography>}
        <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>{archive.archiveReason || t('locations.archives.preserved')}</Typography>
      </PanelCard>;
    })}
  </Box>;
}

function readAddress(value: unknown): string {
  if (!value || typeof value !== 'object') return '';
  const address = value as Record<string, unknown>;
  return [address.line1, address.line2, address.city, [address.state, address.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
}
